import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { CardModule } from "primeng/card";
import { trigger, style, transition, animate } from "@angular/animations";

export interface RevenueCardData {
  title: string;
  amount: number;
  percentageChange: number;
  orders: number;
  ordersChange: number;
  aov: number | null; // Average Order Value (can be null when no orders)
  aovChange: number | null; // Can be null when no orders
  colorClass?: string; // Optional color class for styling
}

@Component({
  selector: "app-revenue-card",
  imports: [CommonModule, CardModule],
  templateUrl: "./revenue-card.component.html",
  styleUrl: "./revenue-card.component.scss",
  animations: [
    trigger("expandCollapse", [
      transition(":enter", [
        style({ height: 0, opacity: 0, overflow: "hidden" }),
        animate(
          "300ms ease-out",
          style({ height: "*", opacity: 1, overflow: "hidden" }),
        ),
      ]),
      transition(":leave", [
        style({ height: "*", opacity: 1, overflow: "hidden" }),
        animate(
          "300ms ease-in",
          style({ height: 0, opacity: 0, overflow: "hidden" }),
        ),
      ]),
    ]),
  ],
})
export class RevenueCardComponent implements OnInit, OnChanges {
  @Input() data!: RevenueCardData;

  // View state
  isExpanded = false;

  // Expose Math for template
  Math = Math;

  // Animated values
  animatedAmount = 0;
  animatedPercentageChange = 0;
  animatedOrders = 0;
  animatedOrdersChange = 0;
  animatedAov = 0;
  animatedAovChange = 0;

  ngOnInit() {
    this.animateNumbers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["data"] && !changes["data"].firstChange) {
      this.animateNumbers();
    }
  }

  private animateNumbers() {
    const steps = 60;

    let currentStep = 0;

    const animate = () => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOutProgress = 1 - Math.pow(1 - progress, 3); // Ease-out cubic

      this.animatedAmount = this.data.amount * easeOutProgress;
      this.animatedPercentageChange =
        this.data.percentageChange * easeOutProgress;
      this.animatedOrders = Math.floor(this.data.orders * easeOutProgress);
      this.animatedOrdersChange = this.data.ordersChange * easeOutProgress;
      this.animatedAov = (this.data.aov ?? 0) * easeOutProgress;
      this.animatedAovChange = (this.data.aovChange ?? 0) * easeOutProgress;

      if (currentStep < steps) {
        requestAnimationFrame(animate);
      } else {
        // Ensure final values are exact
        this.animatedAmount = this.data.amount;
        this.animatedPercentageChange = this.data.percentageChange;
        this.animatedOrders = this.data.orders;
        this.animatedOrdersChange = this.data.ordersChange;
        this.animatedAov = this.data.aov ?? 0;
        this.animatedAovChange = this.data.aovChange ?? 0;
      }
    };

    // Reset to 0 before animating
    this.animatedAmount = 0;
    this.animatedPercentageChange = 0;
    this.animatedOrders = 0;
    this.animatedOrdersChange = 0;
    this.animatedAov = 0;
    this.animatedAovChange = 0;

    requestAnimationFrame(animate);
  }

  /**
   * Truncates a number to specified decimal places WITHOUT any rounding.
   * Just cuts off everything after the specified decimals.
   * Example: truncateToDecimals(44.49830645161905, 2) => 44.49 (not 44.50)
   * Example: truncateToDecimals(-72.353954, 2) => -72.35 (not -72.36)
   */
  private truncateToDecimals(value: number, decimals: number = 2): number {
    const multiplier = Math.pow(10, decimals);
    return Math.trunc(value * multiplier) / multiplier;
  }

  formatCurrency(value: number): string {
    const truncated = this.truncateToDecimals(value, 2);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(truncated);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat("en-US").format(Math.floor(value));
  }

  formatPercentage(value: number): string {
    const truncated = this.truncateToDecimals(value, 2);
    const sign = truncated > 0 ? "+" : "";
    return `${sign}${truncated.toFixed(2)}%`;
  }

  isPositiveChange(value: number | null): boolean {
    return (value ?? 0) > 0;
  }

  hasAovData(): boolean {
    return this.data.aov !== null;
  }

  toggleView(): void {
    this.isExpanded = !this.isExpanded;
  }

  // Computed properties for detailed view
  getDailyAverage(): number {
    // Assuming 30-day period for demo
    return this.data.amount / 30;
  }

  getRevenuePerOrder(): number {
    return this.data.orders > 0 ? this.data.amount / this.data.orders : 0;
  }

  getGrowthVelocity(): string {
    const change = this.data.percentageChange;
    if (change > 100) return "Accelerating";
    if (change > 20) return "Strong Growth";
    if (change > 0) return "Growing";
    if (change > -10) return "Stable";
    return "Declining";
  }

  getGrowthVelocityClass(): string {
    const change = this.data.percentageChange;
    if (change > 100) return "velocity-accelerating";
    if (change > 20) return "velocity-strong";
    if (change > 0) return "velocity-growing";
    if (change > -10) return "velocity-stable";
    return "velocity-declining";
  }
}
